import React, { useState, useEffect, useRef, useMemo } from 'react';
import Sidebar from '../Sidebar';
import {
  FiBookOpen,
  FiHeadphones,
  FiMic,
  FiEdit2,
  FiZap,
  FiEye,
  FiShare2,
  FiArrowRight,
  FiBook,
  FiFileText,
  FiCheck,
  FiAward,
  FiTarget,
  FiMap,
  FiClock,
  FiPlay,
  FiFlag,
  FiTrendingUp,
  FiCheckCircle,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCompletedLessons } from '../../utils/lessonCompletion';
import Tips from './english/Tips';
import './English.css';
import EnglishTestCard from '../../components/EnglishTestCard';
import {
  API_BASE_URL,
  getAccountScopedLocalStorageKey,
  getAuthHeaders,
} from '../../utils/utils';
import { getStoredUser } from '../../utils/authSession';
import {
  CEFR_LEVELS,
  getDaysToTarget,
  getAdmissionInfo,
} from '../../utils/cefrUtils';
import {
  getTodayActivities,
  getJourneyProgress,
  loadJourneyProgress,
  loadJourneyProgressAsync,
  getCurrentJourneyDay,
  calculateDayProgress,
  activityDurationMinutes,
} from '../../utils/languageJourneyData';
import { syncLocalStorageToBackend } from '../../utils/utils';
import { getStoredHoldIntervals } from '../../utils/progressHold';
import { ENGLISH_SIDEBAR_CATEGORIES } from '../../utils/englishCategories';
import {
  LIBRARY_GRAMMAR_LESSONS,
  LIBRARY_VOCABULARY_LESSONS,
  getMocksForCefr,
} from '../../utils/ieltsCatalog';
import { canAccessAcademicTests } from '../../utils/cefrUtils';

// Import JSON data files
import listeningData from '../../assets/mock-data/listening.json';
import readingData from '../../assets/mock-data/reading.json';
import writingData from '../../assets/mock-data/writing.json';
import speakingData from '../../assets/mock-data/speaking.json';
import mockExamData from '../../assets/mock-data/mockexam.json';

// Helper to get test results from localStorage
const getLocalTestResults = (type, testId) => {
  try {
    const user = getStoredUser() || {};
    const userKey = getAccountScopedLocalStorageKey(
      `${type}TestResults_${testId}`,
      user?.email
    );
    const userStored = userKey ? localStorage.getItem(userKey) : null;
    if (userStored) {
      return JSON.parse(userStored);
    }

    // Also check completedTests object (where writing tests are stored on successful submission)
    const completedTestsKey = getAccountScopedLocalStorageKey(
      'completedTests',
      user?.email
    );
    const completedTests = JSON.parse(
      (completedTestsKey && localStorage.getItem(completedTestsKey)) || '{}'
    );

    if (completedTests[type]) {
      const testIdNum = parseInt(testId);
      // IMPORTANT: Only match on testId (the actual test ID), NOT on id (which is the DB record ID)
      const foundTest = completedTests[type].find(
        (t) => t.testId === testIdNum || t.testId === testId
      );
      if (foundTest) {
        return foundTest;
      }
    }

    return null;
  } catch {
    return null;
  }
};

// Helper to save test results to localStorage (fallback)
export const saveTestResultsToLocal = (type, testId, results) => {
  try {
    const user = getStoredUser() || {};
    const userKey = getAccountScopedLocalStorageKey(
      `${type}TestResults_${testId}`,
      user?.email
    );
    if (!userKey) return false;

    localStorage.setItem(
      userKey,
      JSON.stringify({
        ...results,
        testId,
        type,
        completedAt: new Date().toISOString(),
      })
    );
    return true;
  } catch (error) {
    console.error('Error saving test results to localStorage:', error);
    return false;
  }
};

// Helper functions to transform JSON data to match expected format
const transformTestData = (jsonData, type, completedTests) => {
  return jsonData.map((test) => {
    // Check if this test is completed using backend data
    // IMPORTANT: Only match on testId (the actual test ID), NOT on id (which is the DB record ID)
    const backendResult = completedTests[type]?.find(
      (completedTest) => completedTest.testId === test.id
    );

    // Also check localStorage as fallback
    const localResult = getLocalTestResults(type, test.id);

    const isCompleted =
      !!backendResult || !!localResult || test.completed || false;
    const resultData = backendResult || localResult;

    // Handle different score formats for different test types
    let bandScore = null;
    let score = null;
    let totalWords = null;

    if (resultData) {
      // Writing tests have different structure
      if (type === 'writing') {
        const assessmentData = resultData.assessmentData;

        // Try to get band score from multiple sources
        // 1. Direct bandScore (if not null)
        if (
          resultData.bandScore !== null &&
          resultData.bandScore !== undefined
        ) {
          bandScore = resultData.bandScore;
        }
        // 2. assessmentData.overallBandScore (if not null)
        else if (
          assessmentData?.overallBandScore !== null &&
          assessmentData?.overallBandScore !== undefined
        ) {
          bandScore = assessmentData.overallBandScore;
        }
        // 3. Calculate from task1 and task2 scores if overall is null
        else if (
          assessmentData?.task1?.assessment ||
          assessmentData?.task2?.assessment
        ) {
          const task1Band =
            assessmentData?.task1?.assessment?.estimated_band_score;
          const task2Band =
            assessmentData?.task2?.assessment?.estimated_band_score;

          if (
            task1Band !== undefined &&
            task1Band !== null &&
            task2Band !== undefined &&
            task2Band !== null
          ) {
            // IELTS Writing: Task 2 is weighted more (2/3 of overall score)
            bandScore = task1Band / 3 + (task2Band * 2) / 3;
          } else if (task1Band !== undefined && task1Band !== null) {
            bandScore = task1Band;
          } else if (task2Band !== undefined && task2Band !== null) {
            bandScore = task2Band;
          }
        }

        totalWords = resultData.totalWords || null;
        score = totalWords;
      } else {
        // Reading/Listening tests
        bandScore =
          resultData.bandScore !== undefined && resultData.bandScore !== null
            ? resultData.bandScore
            : null;
        score = resultData.score ?? resultData.rawScore ?? null;
      }
    }

    return {
      id: test.id,
      title: test.title,
      completed: isCompleted,
      desc: test.description,
      topic: test.topic,
      tag: getTestTag(test, type),
      // Include result data for displaying scores
      score: score,
      bandScore: bandScore,
      totalWords: totalWords,
      completedAt: resultData?.completedAt || resultData?.created_at || null,
      resultData: resultData,
    };
  });
};

const transformMockExamData = (jsonData) => {
  return jsonData.map((exam) => ({
    id: exam.id,
    title: exam.title,
    completed: exam.completed || false,
    desc: exam.description,
    duration: exam.duration,
    difficulty: exam.difficulty,
    tag: exam.tag,
    tasks: exam.tasks,
    topics: exam.topics,
    lastScore: exam.lastScore,
  }));
};

const getTestTag = (test, type) => {
  // Generate appropriate tags based on test content or type
  switch (type) {
    case 'listening':
      // Check if test has conversation or lecture content
      if (
        test.sections?.some((section) =>
          section.sectionTitle?.toLowerCase().includes('conversation')
        )
      ) {
        return 'Conversation';
      }
      return 'Lecture';
    case 'reading':
      // Check reading passage topics
      if (
        test.description?.toLowerCase().includes('science') ||
        test.sections?.some((section) =>
          section.readingText?.title?.toLowerCase().includes('physics')
        )
      ) {
        return 'Science';
      }
      if (test.description?.toLowerCase().includes('history')) {
        return 'History';
      }
      return 'Academic';
    case 'speaking':
      // Check speaking topics
      if (
        test.sections?.some((section) =>
          section.topics?.some((topic) =>
            topic?.toLowerCase().includes('personal')
          )
        )
      ) {
        return 'Personal';
      }
      if (
        test.sections?.some((section) =>
          section.topics?.some((topic) =>
            topic?.toLowerCase().includes('travel')
          )
        )
      ) {
        return 'Travel';
      }
      return 'Opinion';
    case 'writing':
      // Check writing task types
      if (
        test.sections?.some(
          (section) =>
            section.question?.task?.toLowerCase().includes('chart') ||
            section.question?.task?.toLowerCase().includes('graph')
        )
      ) {
        return 'Charts';
      }
      return 'Academic';
    default:
      return undefined;
  }
};

// Load test data from JSON files
const getMockTests = (completedTests = {}, cefrLevel = 'B1') => {
  const allowAcademic = canAccessAcademicTests(cefrLevel);
  const visibleReading = allowAcademic
    ? readingData
    : readingData.filter((test) => test.format !== 'academic');
  const visibleWriting = allowAcademic
    ? writingData
    : writingData.filter((test) => test.format !== 'academic');
  const visibleListening = allowAcademic
    ? listeningData
    : listeningData.filter((test) => test.format !== 'academic');
  return {
    listening: transformTestData(visibleListening, 'listening', completedTests),
    reading: transformTestData(visibleReading, 'reading', completedTests),
    writing: transformTestData(visibleWriting, 'writing', completedTests),
    speaking: transformTestData(speakingData, 'speaking', completedTests),
    mock: transformMockExamData(getMocksForCefr(cefrLevel)),
  };
};

// Use shared categories from utils
const CATEGORIES = ENGLISH_SIDEBAR_CATEGORIES;

const FILTERS = {
  number: [
    'ALL',
    '66-75',
    '56-65',
    '46-55',
    '36-45',
    '26-35',
    '16-25',
    '6-15',
    '1-5',
  ],
  task: [
    'ALL',
    'Speaking Task 1',
    'Speaking Task 2',
    'Speaking Task 3',
    'Speaking Task 4',
  ],
  status: ['ALL', 'Completed', 'Not completed'],
};

const GRAMMAR_LESSONS = LIBRARY_GRAMMAR_LESSONS;
const VOCABULARY_LESSONS = LIBRARY_VOCABULARY_LESSONS;

const TAGS = {
  reading: ['Academic', 'Science', 'History', 'Technology'],
  listening: ['Conversation', 'Lecture', 'Campus', 'Academic'],
  speaking: ['Personal', 'Opinion', 'Travel', 'Experience'],
  writing: ['Academic', 'Charts', 'Essays', 'Reports'],
  mock: ['Full Test', 'Intermediate', 'Advanced', 'Upper-Intermediate'],
};





// Get completed tests from localStorage (fallback)
const getCompletedTestsFromLocalStorage = () => {
  try {
    const completedTestsKey = getAccountScopedLocalStorageKey('completedTests');
    const completedTests = completedTestsKey
      ? localStorage.getItem(completedTestsKey)
      : null;
    console.log('completedTests from localStorage', completedTests);
    return completedTests
      ? JSON.parse(completedTests)
      : {
          listening: [],
          reading: [],
          writing: [],
          speaking: [],
          mock: [],
        };
  } catch (error) {
    console.error('Error reading completed tests from localStorage:', error);
    return {
      listening: [],
      reading: [],
      writing: [],
      speaking: [],
      mock: [],
    };
  }
};

// Legacy function for backward compatibility
// eslint-disable-next-line no-unused-vars
const getCompletedTests = () => {
  return getCompletedTestsFromLocalStorage();
};

const IELTS = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is enrolled in a journey
  const isEnrolledInJourney = () => {
    try {
      const user = getStoredUser();
      if (!user?.email) return false;
      const journeyData = localStorage.getItem(`languageJourney_${user.email}`);
      if (journeyData) {
        const parsed = JSON.parse(journeyData);
        return parsed.enrolled === true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Default category based on enrollment status
  const getDefaultCategory = () => {
    if (location.state?.activeCategory) return location.state.activeCategory;
    // Default to journey for all users - journey page handles assessment/enrollment flow
    return 'journey';
  };

  // eslint-disable-next-line no-unused-vars
  const [activeCategory, setActiveCategory] = useState(getDefaultCategory());
  const [selectedCategory, setSelectedCategory] =
    useState(getDefaultCategory());
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('ALL');
  const [selectedTag, setSelectedTag] = useState('ALL');

  // Journey state
  const [journeyData, setJourneyData] = useState(null);
  const [journeyLoading, setJourneyLoading] = useState(true);
  const [currentLevel, setCurrentLevel] = useState(null);
  const [needsAssessment, setNeedsAssessment] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isEnrolled, setIsEnrolled] = useState(isEnrolledInJourney());

  // Redirect to appropriate pages based on category
  useEffect(() => {
    if (selectedCategory === 'speaking') {
      navigate('/exams/english/speaking');
    } else if (selectedCategory === 'journey') {
      // Always redirect to journey page - it handles enrollment/assessment flow
      navigate('/exams/english/journey');
    }
  }, [selectedCategory, navigate]);

  // State for completed lessons
  const [completedLessons, setCompletedLessons] = useState({});

  // State for test completion updates
  // eslint-disable-next-line no-unused-vars
  const [testCompletionTrigger, setTestCompletionTrigger] = useState(0);

  // State for completed tests from backend
  const [completedTests, setCompletedTests] = useState({
    listening: [],
    reading: [],
    writing: [],
    speaking: [],
    mock: [],
  });
  // eslint-disable-next-line no-unused-vars
  const [completedTestsLoading, setCompletedTestsLoading] = useState(true);

  // Add ref to track if backend request is in progress
  const backendRequestInProgress = useRef(false);
  const backendRequestCache = useRef(null);
  const cacheTimestamp = useRef(null);

  // Load journey data only if enrolled
  useEffect(() => {
    const loadJourneyData = async () => {
      if (!isEnrolled) {
        setJourneyLoading(false);
        return;
      }

      try {
        setJourneyLoading(true);
        const user = getStoredUser();

        if (!user?.email) {
          setNeedsAssessment(true);
          setJourneyLoading(false);
          return;
        }

        // First, sync any local journey data to backend (ensures other devices get it)
        const localJourney = loadJourneyProgress(user.email);
        if (localJourney && localJourney.enrolled) {
          console.log('Syncing local journey to backend...');
          syncLocalStorageToBackend(user.email, true).catch((err) =>
            console.error('Background sync failed:', err)
          );
        }

        // Try to load existing journey from localStorage, then backend
        const savedJourney = await loadJourneyProgressAsync(user.email);

        if (savedJourney && savedJourney.journey && savedJourney.enrolled) {
          // Pass user email to check for admin day override
          const currentDay = getCurrentJourneyDay(
            savedJourney.startDate,
            user.email,
            savedJourney.journey.length || 60,
            getStoredHoldIntervals()
          );

          // First pass: calculate progress for all days to find the highest day that allows progression
          const daysWithProgress = savedJourney.journey.map((day) => ({
            ...day,
            progress: calculateDayProgress(day),
          }));

          // Find the highest day number where user can proceed to next day
          // This allows progression when course is completed, even without speaking
          // (useful when voice minutes are exhausted)
          let highestProgressionDay = 0;
          daysWithProgress.forEach((day, index) => {
            // User can proceed if: day is fully completed OR canProceedToNextDay is true
            if (
              day.progress.status === 'completed' ||
              day.progress.canProceedToNextDay
            ) {
              highestProgressionDay = Math.max(
                highestProgressionDay,
                index + 1
              );
            }
          });

          // The effective current day is the max of calendar day and (highestProgressionDay + 1)
          const effectiveCurrentDay = Math.max(
            currentDay,
            highestProgressionDay + 1
          );

          const updatedJourney = daysWithProgress.map((day, index) => {
            const dayNumber = index + 1;
            const dayProgress = day.progress;
            let status = day.status;

            if (dayNumber < effectiveCurrentDay && status !== 'completed') {
              status =
                dayProgress.status === 'completed'
                  ? 'completed'
                  : day.activities.some((a) => a.completed)
                    ? 'partial'
                    : 'missed';
            } else if (dayNumber === effectiveCurrentDay) {
              if (dayProgress.status === 'completed') {
                status = 'completed';
              } else if (dayProgress.status === 'partial') {
                status = 'partial';
              } else {
                status = 'current';
              }
            } else if (dayNumber > effectiveCurrentDay) {
              status = 'locked';
            }

            return { ...day, status, progress: dayProgress };
          });

          setJourneyData({
            ...savedJourney,
            journey: updatedJourney,
            currentDay: effectiveCurrentDay,
          });
          setCurrentLevel(CEFR_LEVELS[savedJourney.currentLevel]);
        } else {
          setNeedsAssessment(true);
        }
      } catch (error) {
        console.error('Error loading journey:', error);
        setNeedsAssessment(true);
      } finally {
        setJourneyLoading(false);
      }
    };

    loadJourneyData();
  }, [isEnrolled]);

  // Calculate journey progress
  const journeyProgress = useMemo(() => {
    if (!journeyData?.journey) return null;
    return getJourneyProgress(journeyData.journey);
  }, [journeyData]);

  // Get today's activities
  const todayActivities = useMemo(() => {
    if (!journeyData?.journey || !journeyData.currentDay) return null;
    return getTodayActivities(journeyData.journey, journeyData.currentDay);
  }, [journeyData]);

  useEffect(() => {
    // Set the active category from navigation state if it exists
    if (location.state?.activeCategory) {
      setSelectedCategory(location.state.activeCategory);
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }

    // Load completed lessons
    setCompletedLessons(getCompletedLessons());

    // Load completed tests from backend
    loadCompletedTests();

    // Listen for lesson completion events
    const handleLessonCompleted = () => {
      setCompletedLessons(getCompletedLessons());
    };

    const handleLessonRemoved = () => {
      setCompletedLessons(getCompletedLessons());
    };

    // Listen for test completion events
    const handleTestCompleted = (event) => {
      console.log('Test completed event received:', event.detail);
      // Clear cache to ensure fresh data on next load
      clearBackendCache();
      // Trigger re-render by updating state and reloading completed tests
      setTestCompletionTrigger((prev) => prev + 1);
      loadCompletedTests(true); // Force refresh to get latest data
    };

    window.addEventListener('lessonCompleted', handleLessonCompleted);
    window.addEventListener('lessonRemoved', handleLessonRemoved);
    window.addEventListener('testCompleted', handleTestCompleted);

    return () => {
      window.removeEventListener('lessonCompleted', handleLessonCompleted);
      window.removeEventListener('lessonRemoved', handleLessonRemoved);
      window.removeEventListener('testCompleted', handleTestCompleted);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const loadCompletedTests = async (force = false) => {
    setCompletedTestsLoading(true);
    try {
      if (force) {
        clearBackendCache();
      }
      const completedTestsData = await fetchCompletedTestsFromBackend();
      setCompletedTests(completedTestsData);
    } catch (error) {
      console.error('Error loading completed tests:', error);
    } finally {
      setCompletedTestsLoading(false);
    }
  };

  const tags = TAGS[selectedCategory] || [];
  const diagnosedLevel = (() => {
    try {
      const user = getStoredUser() || {};
      if (!user?.email) return 'B1';
      const journeyData = localStorage.getItem(`languageJourney_${user.email}`);
      if (!journeyData) return 'B1';
      return JSON.parse(journeyData).currentLevel || 'B1';
    } catch {
      return 'B1';
    }
  })();
  const tests = getMockTests(completedTests, diagnosedLevel)[selectedCategory] || [];
  const filteredTests = tests.filter((t) => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()))
      return false;
    if (selectedStatus === 'Completed' && !t.completed) return false;
    if (selectedStatus === 'Not completed' && t.completed) return false;
    const testTags = t.tags || (t.tag ? [t.tag] : []);
    if (selectedTag !== 'ALL' && !testTags.includes(selectedTag)) return false;
    return true;
  });

  const handlePractice = (test) => {
    if (selectedCategory === 'mock') {
      // Handle mock exam differently
      handleMockExam(test);
    } else {
      // Save exam info in localStorage
      console.log('test', test);
      let examInfo;
      if (test.topic) {
        examInfo = {
          id: test.id,
          title: test.title,
          type: selectedCategory,
          startTime: new Date().toISOString(),
          topic: test.topic,
        };
      } else {
        examInfo = {
          id: test.id,
          title: test.title,
          type: selectedCategory,
          startTime: new Date().toISOString(),
        };
      }
      const englishExamKey = getAccountScopedLocalStorageKey('englishExam');
      if (!englishExamKey) return;
      localStorage.setItem(englishExamKey, JSON.stringify(examInfo));

      // Navigate to the appropriate exam page
      navigate(`/exams/english/${selectedCategory}`);
    }
  };

  const handleMockExam = (mockExam) => {
    // Save mock exam info in localStorage
    const mockExamInfo = {
      id: mockExam.id,
      title: mockExam.title,
      tasks: mockExam.tasks,
      currentTaskIndex: 0,
      startTime: new Date().toISOString(),
      isBreak: false,
      breakStartTime: null,
      scores: {},
      status: 'in-progress',
    };
    const mockExamKey = getAccountScopedLocalStorageKey('mockExam');
    if (!mockExamKey) return;
    localStorage.setItem(mockExamKey, JSON.stringify(mockExamInfo));

    // Start with the first task (Listening)
    navigate('/exams/english/listening', {
      state: {
        isMockExam: true,
        mockExamInfo: mockExamInfo,
      },
    });
  };

  const handleViewResults = (test) => {
    // Handle both regular test objects and progress test objects
    const testType = test.testType || selectedCategory;
    const testId = test.testId || test.id;
    console.log('handleViewResults', testType, testId, test);
    // Navigate to results page for the specific test
    navigate(`/exams/english/${testType}/results/${testId}`);
  };

  const handleReviewTest = (test) => {
    // Navigate to test page in review mode
    const testType = test.testType || selectedCategory;
    const testId = test.testId || test.id;

    console.log('handleReviewTest - testType:', testType, 'testId:', testId);

    // Get data from multiple sources and merge them
    let completedTestData = null;
    let detailedResultData = null;

    // First, check completedTests in localStorage (has basic info like score, completedAt)
    const completedTestsKey = getAccountScopedLocalStorageKey('completedTests');
    const completedTests = JSON.parse(
      (completedTestsKey && localStorage.getItem(completedTestsKey)) || '{}'
    );
    if (completedTests[testType]) {
      const testIdNum = parseInt(testId);
      completedTestData = completedTests[testType].find(
        (t) =>
          t.testId === testIdNum ||
          t.testId === testId ||
          t.id === testIdNum ||
          t.id === testId
      );
      console.log(
        'handleReviewTest - Found in completedTests:',
        completedTestData
      );
    }

    // Always try to get detailed results from localStorage (has answers, results details)
    detailedResultData = getLocalTestResults(testType, testId);
    console.log(
      'handleReviewTest - Found via getLocalTestResults:',
      detailedResultData
    );

    // Merge data: detailed results take priority, then completedTests, then test.resultData
    let resultData = {
      ...(test.resultData || {}),
      ...(completedTestData || {}),
      ...(detailedResultData || {}),
    };

    // If still no data, fall back to test.resultData
    if (!resultData || Object.keys(resultData).length === 0) {
      resultData = test.resultData;
      console.log('handleReviewTest - Using test.resultData:', resultData);
    }

    // Save exam info for review mode
    const examInfo = {
      id: testId,
      title: test.title,
      type: testType,
      reviewMode: true,
    };
    const englishExamKey = getAccountScopedLocalStorageKey('englishExam');
    if (englishExamKey) {
      localStorage.setItem(englishExamKey, JSON.stringify(examInfo));
    }

    // Build savedResult with all necessary data
    const savedResult = {
      ...resultData,
      testId: testId,
      // Ensure answers are included (for reading/listening review mode)
      answers: resultData?.answers || null,
      // Ensure results/details are included (for reading review mode)
      results: resultData?.results || resultData?.details || null,
      rawScore: resultData?.rawScore || resultData?.score || 0,
      // Ensure essays are included (for writing review mode)
      essays: resultData?.essays || null,
      // Ensure taskData is included
      taskData: resultData?.taskData || resultData?.task_data || null,
      // Ensure assessmentData is included
      assessmentData:
        resultData?.assessmentData || resultData?.assessment_data || null,
    };

    console.log('handleReviewTest - Final savedResult:', savedResult);
    console.log('handleReviewTest - Answers:', savedResult.answers);
    console.log('handleReviewTest - Results:', savedResult.results);
    console.log('handleReviewTest - Essays:', savedResult.essays);
    console.log(
      'handleReviewTest - AssessmentData:',
      savedResult.assessmentData
    );

    // Navigate to the test page with review state
    navigate(`/exams/english/${testType}`, {
      state: {
        reviewMode: true,
        savedResult: savedResult,
      },
    });
  };

  const handleShare = (test) => {
    // Create sharing functionality
    const shareText = `Check out this ${selectedCategory} test: ${test.title}`;
    const shareUrl = `${window.location.origin}/exams/english/${selectedCategory}`;

    // Check if Web Share API is supported
    if (navigator.share) {
      navigator
        .share({
          title: test.title,
          text: shareText,
          url: shareUrl,
        })
        .catch(console.error);
    } else {
      // Fallback: copy to clipboard
      const shareContent = `${shareText}\n${shareUrl}`;
      navigator.clipboard
        .writeText(shareContent)
        .then(() => {
          alert(
            'Link copied to clipboard! You can now share it via email, social media, or messaging apps.'
          );
        })
        .catch(() => {
          // Fallback for older browsers
          prompt('Copy this link to share:', shareContent);
        });
    }
  };

  // Helper function to get user's diagnosed CEFR level
  const getUserDiagnosedLevel = () => {
    try {
      const user = getStoredUser() || {};
      if (user?.email) {
        const journeyData = localStorage.getItem(
          `languageJourney_${user.email}`
        );
        if (journeyData) {
          const parsed = JSON.parse(journeyData);
          return parsed.currentLevel || 'B1'; // Default to B1 if not set
        }
      }
      return 'B1'; // Default level
    } catch {
      return 'B1';
    }
  };

  const renderGrammarLessons = () => {
    return (
      <div className="grammar-lessons">
        <h1 className="grammar-title">Master English Grammar</h1>
        <p className="grammar-subtitle">
          Build a strong foundation with our targeted grammar lessons.
        </p>
        <div className="grammar-lessons-grid">
          {GRAMMAR_LESSONS.map((lesson) => {
            const isCompleted = !!completedLessons[`grammar-${lesson.id}`];
            return (
              <div
                key={lesson.id}
                className={`grammar-lesson-card ${
                  isCompleted ? 'completed' : ''
                }`}
                onClick={() => navigate(lesson.path)}
              >
                <div className="grammar-lesson-header">
                  <div className="grammar-lesson-title-group">
                    <FiFileText className="grammar-lesson-title-icon" />
                    <h3 className="grammar-lesson-title">{lesson.title}</h3>
                  </div>
                  {isCompleted && (
                    <span className="lesson-completed-icon">
                      <FiCheck />
                    </span>
                  )}
                </div>
                <p className="grammar-lesson-description">
                  {lesson.description}
                </p>
                <div className="grammar-lesson-footer">
                  <button className="grammar-lesson-button">
                    {isCompleted ? 'Review Lesson' : 'Start Lesson'}{' '}
                    <FiArrowRight />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderVocabularyLessons = () => {
    return (
      <div className="grammar-lessons">
        <h2 className="grammar-title">Vocabulary Lessons</h2>
        <p className="grammar-subtitle">
          Select a lesson to expand your vocabulary
        </p>

        <div className="grammar-lessons-grid">
          {VOCABULARY_LESSONS.map((lesson) => {
            const isCompleted = !!completedLessons[`vocabulary-${lesson.id}`];

            return (
              <div
                key={lesson.id}
                className={`grammar-lesson-card${
                  isCompleted ? ' completed' : ''
                }`}
                onClick={() => navigate(lesson.path)}
              >
                <div className="grammar-lesson-header">
                  <div className="grammar-lesson-title-group">
                    <FiBook className="grammar-lesson-title-icon" />
                    <h3 className="grammar-lesson-title">{lesson.title}</h3>
                  </div>
                  {isCompleted && (
                    <span className="lesson-completed-icon">
                      <FiCheck />
                    </span>
                  )}
                </div>
                <p className="grammar-lesson-description">
                  {lesson.description}
                </p>
                <div className="grammar-lesson-footer">
                  <button className="grammar-lesson-button">
                    {isCompleted ? 'Review Lesson' : 'Start Lesson'}{' '}
                    <FiArrowRight />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Handle starting an activity from the journey
  const handleStartJourneyActivity = (activity) => {
    if (activity.type === 'speaking') {
      navigate('/exams/english/speaking', {
        state: {
          topic: activity.topic,
          topicCategory: activity.category,
          fromJourney: true,
        },
      });
    } else if (activity.type === 'grammar') {
      setSelectedCategory('grammar');
    } else if (activity.type === 'vocabulary') {
      setSelectedCategory('vocabulary');
    } else if (activity.type === 'checkpoint') {
      navigate('/exams/english/speaking', {
        state: {
          isCheckpoint: true,
          fromJourney: true,
        },
      });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const renderJourney = () => {
    // Loading state
    if (journeyLoading) {
      return (
        <div className="journey-main-container">
          <div className="journey-loading-inline">
            <div className="journey-loading-spinner"></div>
            <p>Loading your learning journey...</p>
          </div>
        </div>
      );
    }

    // Needs assessment state
    if (needsAssessment) {
      return (
        <div className="journey-main-container">
          <div className="journey-assessment-card">
            <div className="assessment-hero">
              <div className="assessment-icon-large">
                <FiMic />
              </div>
              <h1>Start Your English Journey</h1>
              <p>
                A short speaking test with the AI coach. About 10 minutes.
              </p>
            </div>

            <div className="assessment-features">
              <div className="feature-item">
                <div className="feature-icon">
                  <FiTarget />
                </div>
                <div className="feature-text">
                  <h4>Personalized Path</h4>
                  <p>60-day journey tailored to your level</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <FiClock />
                </div>
                <div className="feature-text">
                  <h4>9 Minutes Daily</h4>
                  <p>Speaking practice with AI coach</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <FiTrendingUp />
                </div>
                <div className="feature-text">
                  <h4>Track Progress</h4>
                  <p>Weekly checkpoints and tests</p>
                </div>
              </div>
              <div className="feature-item">
                <div className="feature-icon">
                  <FiAward />
                </div>
                <div className="feature-text">
                  <h4>Graduate Ready</h4>
                  <p>Reach B2-C1 for grad programs</p>
                </div>
              </div>
            </div>

            <button
              className="btn-lwz-primary assessment-start-btn"
              onClick={() => navigate('/exams/english/speaking')}
            >
              <FiMic /> Start Assessment
            </button>

            <p className="assessment-note">
              Or explore our courses below while you prepare
            </p>
          </div>

          {/* Quick Access Cards */}
          <div className="journey-quick-access">
            <h3>Explore Courses</h3>
            <div className="quick-access-grid">
              <div
                className="quick-access-card"
                onClick={() => setSelectedCategory('grammar')}
              >
                <FiFileText className="quick-access-icon" />
                <h4>Grammar</h4>
                <p>Master English grammar rules</p>
                <span className="quick-access-count">
                  {GRAMMAR_LESSONS.length} lessons
                </span>
              </div>
              <div
                className="quick-access-card"
                onClick={() => setSelectedCategory('vocabulary')}
              >
                <FiBook className="quick-access-icon" />
                <h4>Vocabulary</h4>
                <p>Expand your word knowledge</p>
                <span className="quick-access-count">
                  {VOCABULARY_LESSONS.length} lessons
                </span>
              </div>
              <div
                className="quick-access-card"
                onClick={() => navigate('/exams/english/speaking')}
              >
                <FiMic className="quick-access-icon" />
                <h4>Speaking</h4>
                <p>Practice with AI coach</p>
                <span className="quick-access-count">Unlimited practice</span>
              </div>
              <div
                className="quick-access-card"
                onClick={() => setSelectedCategory('listening')}
              >
                <FiHeadphones className="quick-access-icon" />
                <h4>Listening</h4>
                <p>Improve comprehension</p>
                <span className="quick-access-count">Practice tests</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Journey view with data
    const targetLevel = journeyData?.targetLevel || 'C1';
    const admissionInfo = currentLevel
      ? getAdmissionInfo(currentLevel.code)
      : null;

    return (
      <div className="journey-main-container">
        {/* Journey Header */}
        <div className="journey-main-header">
          <div className="journey-header-left">
            <h1>
              <FiMap className="journey-header-icon" />
              Your English Journey
            </h1>
            <p className="journey-header-subtitle">
              {currentLevel?.code} → {targetLevel} •{' '}
              {getDaysToTarget(currentLevel?.code, targetLevel)} days to go
            </p>
          </div>
          <button
            className="btn-lwz-secondary"
            onClick={() => navigate('/exams/english/journey')}
          >
            <FiArrowRight /> Full Journey View
          </button>
        </div>

        {/* Progress Overview */}
        {journeyProgress && (
          <div className="journey-progress-cards">
            <div className="progress-card">
              <div className="progress-card-value">
                Day {journeyData?.currentDay || 1}
              </div>
              <div className="progress-card-label">Current</div>
            </div>
            <div className="progress-card">
              <div className="progress-card-value">
                {journeyProgress.completedDays}
              </div>
              <div className="progress-card-label">Days Done</div>
            </div>
            <div className="progress-card">
              <div className="progress-card-value">
                Week {journeyProgress.currentWeek}
              </div>
              <div className="progress-card-label">
                of {journeyProgress.totalWeeks}
              </div>
            </div>
            <div className="progress-card highlight">
              <div className="progress-card-value">
                {journeyProgress.daysProgress}%
              </div>
              <div className="progress-card-label">Progress</div>
            </div>
          </div>
        )}

        {/* CEFR Level Display */}
        {currentLevel && (
          <div className="journey-cefr-display">
            <div
              className="cefr-level-badge-large"
              style={{ background: currentLevel.gradient }}
            >
              <span className="cefr-code">{currentLevel.code}</span>
              <span className="cefr-name">{currentLevel.name}</span>
            </div>
            <div className="cefr-info">
              <p className="cefr-description">{currentLevel.description}</p>
              {admissionInfo && (
                <div
                  className={`admission-badge ${admissionInfo.eligible ? 'eligible' : 'not-eligible'}`}
                >
                  {admissionInfo.eligible ? (
                    <>
                      <FiCheckCircle /> Graduate Program Ready
                    </>
                  ) : (
                    <>
                      <FiTarget /> Target: B2-C1 for graduate programs
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Today's Activities */}
        {todayActivities && (
          <div className="journey-today-section">
            <div className="today-section-header">
              <h2>
                <FiZap /> Today's Practice
              </h2>
              <span className="today-duration">
                <FiClock /> {todayActivities.totalDuration} min
              </span>
            </div>
            <div className="today-activities-grid">
              {todayActivities.activities.map((activity) => (
                <div
                  key={activity.id}
                  className={`today-activity-card ${activity.completed ? 'completed' : ''}`}
                >
                  <div className="activity-card-icon">
                    {activity.type === 'speaking' && <FiMic />}
                    {activity.type === 'grammar' && <FiFileText />}
                    {activity.type === 'vocabulary' && <FiBook />}
                    {activity.type === 'checkpoint' && <FiFlag />}
                  </div>
                  <div className="activity-card-info">
                    <h4>{activity.name}</h4>
                    <p>{activity.description}</p>
                    <span className="activity-time">
                      {activityDurationMinutes(activity)} min
                    </span>
                  </div>
                  {activity.completed ? (
                    <div className="activity-done-badge">
                      <FiCheckCircle /> Done
                    </div>
                  ) : (
                    <button
                      className="btn-lwz-primary activity-btn"
                      onClick={() => handleStartJourneyActivity(activity)}
                    >
                      <FiPlay /> Start
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Access to Courses */}
        <div className="journey-courses-section">
          <h3>Continue Learning</h3>
          <div className="courses-grid">
            <div
              className="course-card grammar"
              onClick={() => setSelectedCategory('grammar')}
            >
              <FiFileText className="course-icon" />
              <div className="course-info">
                <h4>Grammar Lessons</h4>
                <p>
                  {
                    Object.keys(completedLessons).filter((k) =>
                      k.startsWith('grammar')
                    ).length
                  }{' '}
                  / {GRAMMAR_LESSONS.length} completed
                </p>
              </div>
              <FiArrowRight className="course-arrow" />
            </div>
            <div
              className="course-card vocabulary"
              onClick={() => setSelectedCategory('vocabulary')}
            >
              <FiBook className="course-icon" />
              <div className="course-info">
                <h4>Vocabulary Lessons</h4>
                <p>
                  {
                    Object.keys(completedLessons).filter((k) =>
                      k.startsWith('vocabulary')
                    ).length
                  }{' '}
                  / {VOCABULARY_LESSONS.length} completed
                </p>
              </div>
              <FiArrowRight className="course-arrow" />
            </div>
            <div
              className="course-card speaking"
              onClick={() => navigate('/exams/english/speaking')}
            >
              <FiMic className="course-icon" />
              <div className="course-info">
                <h4>Speaking Practice</h4>
                <p>9-minute AI conversation</p>
              </div>
              <FiArrowRight className="course-arrow" />
            </div>
            <div
              className="course-card tests"
              onClick={() => setSelectedCategory('listening')}
            >
              <FiHeadphones className="course-icon" />
              <div className="course-info">
                <h4>Practice Tests</h4>
                <p>Listening, Reading, Writing</p>
              </div>
              <FiArrowRight className="course-arrow" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMockExamCard = (mockExam) => {
    const getDifficultyClass = (difficulty) => {
      switch (difficulty?.toLowerCase()) {
        case 'easy':
          return 'difficulty-easy';
        case 'intermediate':
          return 'difficulty-medium';
        case 'advanced':
          return 'difficulty-hard';
        default:
          return '';
      }
    };

    return (
      <div key={mockExam.id} className="english-mock-exam-card">
        <div className="english-mock-exam-header">
          <div className="english-mock-exam-title-section">
            <h3 className="english-mock-exam-title">{mockExam.title}</h3>
            <div className="english-mock-exam-badges">
              {mockExam.difficulty && (
                <span
                  className={`english-mock-exam-difficulty ${getDifficultyClass(
                    mockExam.difficulty
                  )}`}
                >
                  {mockExam.difficulty}
                </span>
              )}
              <span className="english-mock-exam-duration">
                {mockExam.duration}
              </span>
              {mockExam.completed && (
                <span className="english-mock-exam-completed">
                  <FiCheck /> Completed
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="english-mock-exam-description">{mockExam.desc}</p>

        {mockExam.lastScore && (
          <div className="english-mock-exam-scores">
            <h4>Last Score:</h4>
            <div className="english-score-breakdown">
              <span className="english-overall-score">
                Overall: {mockExam.lastScore.overall}
              </span>
              <span>Listening: {mockExam.lastScore.listening}</span>
              <span>Reading: {mockExam.lastScore.reading}</span>
              <span>Writing: {mockExam.lastScore.writing}</span>
              <span>Speaking: {mockExam.lastScore.speaking}</span>
            </div>
          </div>
        )}

        <div className="english-mock-exam-tasks">
          <h4>Test Structure:</h4>
          <div className="english-tasks-grid">
            {mockExam.tasks.map((task, index) => (
              <div key={index} className="english-task-item">
                <div className="english-task-skill">
                  {task.skill === 'Listening' && <FiHeadphones />}
                  {task.skill === 'Reading' && <FiBookOpen />}
                  {task.skill === 'Writing' && <FiEdit2 />}
                  {task.skill === 'Speaking' && <FiMic />}
                  <span>{task.skill}</span>
                </div>
                <div className="english-task-details">
                  <span className="english-task-duration">{task.duration}</span>
                  <span className="english-task-description">
                    {task.description}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="english-mock-exam-topics">
          <h4>Topics:</h4>
          <div className="english-topics-list">
            {mockExam.topics.map((topic, index) => (
              <span key={index} className="english-topic-tag">
                {topic}
              </span>
            ))}
          </div>
        </div>

        <div className="english-mock-exam-actions">
          <button
            className="btn-lwz-primary english-mock-start-btn"
            onClick={() => handlePractice(mockExam)}
          >
            <FiZap />{' '}
            {mockExam.completed ? 'Retake Mock Exam' : 'Start Mock Exam'}
          </button>
          {mockExam.completed ? (
            <>
              <button
                className="btn-lwz-outline"
                onClick={() => handleViewResults(mockExam)}
              >
                <FiEye /> View Results
              </button>
              <button
                className="btn-lwz-outline"
                onClick={() => handleShare(mockExam)}
              >
                <FiShare2 /> Share
              </button>
            </>
          ) : (
            <button
              className="btn-lwz-outline"
              onClick={() => handleShare(mockExam)}
            >
              <FiShare2 /> Share
            </button>
          )}
        </div>
      </div>
    );
  };

  // Fetch completed tests from backend
  const fetchCompletedTestsFromBackend = async () => {
    try {
      // Check if request is already in progress
      if (backendRequestInProgress.current) {
        console.log('Backend request already in progress, waiting...');
        // Wait for the existing request to complete
        while (backendRequestInProgress.current) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        // Return cached result if available
        if (backendRequestCache.current) {
          console.log('Returning cached backend result');
          return backendRequestCache.current;
        }
      }

      // Check if we have a recent cached result (within 30 seconds)
      const now = Date.now();
      if (
        backendRequestCache.current &&
        cacheTimestamp.current &&
        now - cacheTimestamp.current < 30000
      ) {
        console.log('Returning cached backend result (recent)');
        return backendRequestCache.current;
      }

      // Set loading flag
      backendRequestInProgress.current = true;

      const user = getStoredUser();
      if (!user?.email) {
        throw new Error('User email not found');
      }

      console.log('Fetching completed tests from backend...');
      const response = await fetch(
        `${API_BASE_URL}/api/get-completed-tests`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const backendData = await response.json();
        console.log('Backend completed tests:', backendData);

        if (backendData.success && backendData.completedTests) {
          // Cache the result
          backendRequestCache.current = backendData.completedTests;
          cacheTimestamp.current = now;
          return backendData.completedTests;
        } else {
          throw new Error('No completed tests found in backend response');
        }
      } else {
        throw new Error(`Backend request failed: ${response.status}`);
      }
    } catch (error) {
      console.log(
        'Backend completed tests not found, using localStorage fallback...',
        error
      );
      const fallbackData = getCompletedTestsFromLocalStorage();
      // Cache the fallback result too
      backendRequestCache.current = fallbackData;
      cacheTimestamp.current = Date.now();
      return fallbackData;
    } finally {
      // Always clear the loading flag
      backendRequestInProgress.current = false;
    }
  };

  // Function to clear backend cache (useful for forced refresh)
  const clearBackendCache = () => {
    backendRequestCache.current = null;
    cacheTimestamp.current = null;
    backendRequestInProgress.current = false;
    console.log('Backend cache cleared');
  };

  return (
    <div className="english-layout">
      <Sidebar />
      <main className="english-main">
        {selectedCategory === 'journey' ? null : selectedCategory ===
          'grammar' ? (
          renderGrammarLessons()
        ) : selectedCategory === 'vocabulary' ? (
          renderVocabularyLessons()
        ) : selectedCategory === 'speaking' ? null : ( // Speaking is handled by direct navigation in the sidebar click handler
          <>
            <Tips category={selectedCategory} />
            <div
              className="english-filters"
              style={{
                background: 'var(--liwaza-surface-container)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                borderRadius: '12px',
              }}
            >
              <div className="english-filter-group">
                <span style={{ color: 'var(--liwaza-secondary)' }}>Tags:</span>
                <button
                  className={`lwz-badge${selectedTag === 'ALL' ? ' lwz-badge-selected' : ''}`}
                  onClick={() => setSelectedTag('ALL')}
                  type="button"
                >
                  ALL
                </button>
                {tags.map((tag) => (
                  <button
                    key={tag}
                    className={`lwz-badge${selectedTag === tag ? ' lwz-badge-selected' : ''}`}
                    onClick={() => setSelectedTag(tag)}
                    type="button"
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="english-filter-group">
                <span style={{ color: 'var(--liwaza-secondary)' }}>
                  Completion Status:
                </span>
                {FILTERS.status.map((f) => (
                  <button
                    key={f}
                    className={`lwz-badge${selectedStatus === f ? ' lwz-badge-selected' : ''}`}
                    onClick={() => setSelectedStatus(f)}
                    type="button"
                  >
                    {f}
                  </button>
                ))}
              </div>
              <div className="english-filter-group english-search-group">
                <span style={{ color: 'var(--liwaza-secondary)' }}>
                  Search:
                </span>
                <input
                  className="english-search"
                  placeholder="..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    background: 'var(--liwaza-surface-container)',
                    color: 'var(--liwaza-on-surface)',
                    border: '1.5px solid var(--liwaza-outline-variant)',
                  }}
                />
              </div>
            </div>
            <div className="english-test-cards">
              {filteredTests.map((test) =>
                selectedCategory === 'mock' ? (
                  renderMockExamCard(test)
                ) : (
                  <EnglishTestCard
                    key={test.id}
                    test={test}
                    testType={selectedCategory}
                    onReview={handleReviewTest}
                    onRetake={handlePractice}
                    onShare={handleShare}
                    userLevel={getUserDiagnosedLevel()}
                  />
                )
              )}
            </div>
          </>
        )}
      </main>
      <aside className="english-sidebar-right">
        <div
          className="english-sidebar-header"
          style={{
            paddingBottom: '1rem',
            borderBottom: '1px solid var(--liwaza-outline-variant)',
          }}
        >
          <h2
            style={{
              color: 'var(--liwaza-primary)',
              fontWeight: 600,
              fontSize: '1.25rem',
              margin: 0,
              marginBottom: '0.5rem',
            }}
          >
            IELTS Test Sections
          </h2>
          <p
            className="english-sidebar-subtitle"
            style={{
              color: 'var(--liwaza-secondary)',
              fontSize: '0.875rem',
              margin: 0,
            }}
          >
            Select a section to start practicing
          </p>
        </div>
        <nav className="english-sidebar-nav">
          {CATEGORIES.map((cat) =>
            cat.type === 'separator' ? (
              <div key={cat.key} className="english-sidebar-separator" />
            ) : (
              <div
                key={cat.key}
                className={`english-sidebar-item${selectedCategory === cat.key ? ' active' : ''}`}
                onClick={() => {
                  if (cat.key === 'progression') {
                    navigate('/exams/english/progression');
                  } else if (cat.key === 'speaking') {
                    navigate('/exams/english/speaking');
                  } else if (cat.key === 'journey') {
                    navigate('/exams/english/journey');
                  } else {
                    setSelectedCategory(cat.key);
                  }
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {cat.icon}
                </span>
                <span style={{ fontSize: 15 }}>{cat.label}</span>
                {cat.tag && (
                  <span
                    className={`english-sidebar-tag${cat.tag === 'New' ? ' new' : ''}`}
                    style={{
                      background: 'var(--liwaza-secondary-container)',
                      color: 'var(--liwaza-on-secondary-container)',
                      marginLeft: 12,
                    }}
                  >
                    {cat.tag}
                  </span>
                )}
              </div>
            )
          )}
        </nav>
      </aside>
    </div>
  );
};

export default IELTS;
